import { customerInformationStrings as S } from "./CustomerInformation.strings";

/** Recency band of a member's last login, as shown in the Customer Information list. */
export type LastLoginBand = "recent" | "stale" | "old" | "never" | "noAccess";

const DAY_MS = 24 * 60 * 60 * 1000;

/** Colour of the band's dot; the date text always carries the meaning too. */
export const LAST_LOGIN_DOT_COLOR: Record<LastLoginBand, string> = {
  recent: "#16A34A",
  stale: "#D97706",
  old: "#DC2626",
  never: "#9CA3AF",
  noAccess: "#9CA3AF",
};

const parse = (lastLogin: string | null | undefined): Date | null => {
  if (!lastLogin) return null;
  const date = new Date(lastLogin);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** Whole calendar days between the login and now (0 = today). */
const daysSince = (login: Date, now: Date): number =>
  Math.max(0, Math.round((startOfDay(now) - startOfDay(login)) / DAY_MS));

/**
 * Band for the dot: green up to 7 days, amber 8–30, red over 30, gray when the
 * member never logged in or has no access (status `inactive`).
 */
export const lastLoginBand = (
  lastLogin: string | null | undefined,
  status: string | null | undefined,
  now: Date = new Date(),
): LastLoginBand => {
  const login = parse(lastLogin);
  if (!login) return "never";
  if ((status ?? "").toLowerCase() === "inactive") return "noAccess";
  const days = daysSince(login, now);
  if (days <= 7) return "recent";
  if (days <= 30) return "stale";
  return "old";
};

/** Human label for the last login ("Today, 12:43", "Yesterday", "12 days ago", "3 months ago", "Never"). */
export const formatLastLogin = (
  lastLogin: string | null | undefined,
  now: Date = new Date(),
): string => {
  const login = parse(lastLogin);
  if (!login) return S.lastLogin.never;
  const days = daysSince(login, now);
  if (days === 0) {
    const time = login.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return S.lastLogin.today.replace("{time}", time);
  }
  if (days === 1) return S.lastLogin.yesterday;
  if (days < 60) return S.lastLogin.daysAgo.replace("{count}", String(days));
  return S.lastLogin.monthsAgo.replace("{count}", String(Math.floor(days / 30)));
};

/** Tooltip with the exact date and time, or the "never" sentence. */
export const lastLoginTooltip = (lastLogin: string | null | undefined): string => {
  const login = parse(lastLogin);
  return login
    ? S.lastLogin.exactTooltip.replace("{date}", login.toLocaleString())
    : S.lastLogin.neverTooltip;
};
